import {
  cacheKeys,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  SlideAction,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { DUPLICATE_WORKSPACE } from '../operations/duplicateWorkspace';
import {
  DuplicateWorkspaceForm,
  SourceWorkspaceData,
} from './duplicateWorkspaceForm';
import { Workspaces } from './workspaces';

const GET_WORKSPACE_FOR_DUPLICATE = `
  query GetWorkspaceForDuplicate($filter: ListWorkspacesFilter, $page: PageRequest) {
    workspace {
      listWorkspaces(filter: $filter, page: $page) {
        items {
          id
          name
          type
          activitiesCount
          assetsCount
          routinesCount
          peopleCount
          alerts
          groupsCount
        }
      }
    }
  }
`;

export const WorkspaceDuplicatePage = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const onCompleteFnRef = useRef<(() => void) | undefined>(undefined);
  const queryClient = useQueryClient();

  // Fetch the source workspace by ID
  const { data, isLoading } = useCustomQuery({
    query: GET_WORKSPACE_FOR_DUPLICATE,
    cacheKey: ['workspace', 'duplicate-source', workspaceId],
    variables: {
      filter: { id: workspaceId },
      page: { limit: 1, offset: 0 },
    },
  });

  const sourceWorkspace: SourceWorkspaceData | undefined = useMemo(() => {
    const items = data?.workspace?.listWorkspaces?.items;
    if (!items || items.length === 0) return undefined;
    const ws = items[0];
    return {
      id: ws.id,
      name: ws.name,
      type: ws.type,
      activitiesCount: ws.activitiesCount ?? 0,
      assetsCount: ws.assetsCount ?? 0,
      routinesCount: ws.routinesCount ?? 0,
      peopleCount: ws.peopleCount ?? 0,
      alerts: ws.alerts ?? 0,
      groupsCount: ws.groupsCount ?? 0,
    };
  }, [data]);

  const { mutateAsync } = useCustomMutation({
    query: DUPLICATE_WORKSPACE,
  });

  const handleSubmit = async (data: any) => {
    await mutateAsync({ input: data });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('workspace').all,
    });
    onCompleteFnRef.current?.();
  };

  const rightColumn = {
    title: 'workspace.duplicate.title',
    mainComponent:
      !isLoading && sourceWorkspace ? (
        <>
          <DuplicateWorkspaceForm
            sourceWorkspace={sourceWorkspace}
            onSubmit={handleSubmit}
          />
          <BackHandlerRegistrar onCompleteFnRef={onCompleteFnRef} />
        </>
      ) : (
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <LoadingIndicator />
        </View>
      ),
    showBackButton: true,
    disableScroll: true,
    onBackButtonPress: () => onCompleteFnRef.current?.(),
  };

  return <Workspaces rightColumn={rightColumn} viewMode={ViewMode.DUAL} />;
};

const BackHandlerRegistrar = ({
  onCompleteFnRef,
}: {
  onCompleteFnRef: React.MutableRefObject<(() => void) | undefined>;
}) => {
  const navigate = useNavigate();
  const { onSlideChange } = useSlideLayout();

  onCompleteFnRef.current = async () => {
    await onSlideChange(SlideAction.SINGLE);
    navigate('/workspaces');
  };

  return null;
};
